// src/shared/ui/RankingTable.tsx
//
// Reusable, data-dense leaderboard table for the asset-ranking suite.
// Renders the daily cross-sectional z-scores + the composite Forge
// Ultra Signal produced by the backend scoring engine. Institutional
// "heat" colouring on the z-score badges lets a user read factor
// strength at a glance (green = strong / positive, red = weak / negative).
//
// Data source: GET /api/v1/ranking/top-assets — the `rankings` array of
// the payload. The RankingRow shape below mirrors the backend contract
// in app/core/cache_keys.py (_TOP_SIGNALS_ROW_KEYS) and the
// AssetDailyRanking ORM model exactly; keep them in lock-step.

// Matches the exact JSON payload from GET /api/v1/ranking/top-assets
export interface RankingRow {
    symbol: string;
    sector: string;
    forge_ultra_signal: number;
    annualized_volatility: number;
    momentum_z_score: number;
    fundamental_z_score: number;
    sentiment_z_score: number;
    universe_rank: number;
    sector_rank: number;
}

interface RankingTableProps {
    data: RankingRow[];
    isLoading?: boolean;
}

export const ScoreBadge = ({ score }: { score: number }) => {
    // Determine color based on standard deviations.
    let colorClass =
        'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
    if (score >= 1.5)
        colorClass =
            'bg-green-100 text-green-700 dark:bg-emerald-900/40 dark:text-emerald-400';
    else if (score >= 0.5)
        colorClass =
            'bg-green-50 text-green-600 dark:bg-emerald-900/20 dark:text-emerald-500';
    else if (score <= -1.5)
        colorClass =
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
    else if (score <= -0.5)
        colorClass =
            'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-500';

    return (
        <span
            className={`px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide ${colorClass}`}
        >
            {score > 0 ? '+' : ''}
            {score.toFixed(2)}
        </span>
    );
};

export const RankingTable = ({ data, isLoading }: RankingTableProps) => {
    if (isLoading) {
        return (
            <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
                Loading quantitative data...
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-slate-50 dark:bg-slate-950/50 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                        <th scope="col" className="px-6 py-4 font-semibold">
                            Rank
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold">
                            Symbol
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold">
                            Sector
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold text-right">
                            Forge Ultra Signal
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold text-center">
                            Momentum (z)
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold text-center">
                            Quality (z)
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold text-center">
                            Sentiment (z)
                        </th>
                        <th scope="col" className="px-6 py-4 font-semibold text-right">
                            Vol (Ann.)
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={8}
                                className="px-6 py-16 text-center text-slate-500 dark:text-slate-400"
                            >
                                No ranking data available yet. The nightly
                                scoring run may still be pending.
                            </td>
                        </tr>
                    ) : (
                        data.map((row) => (
                            <tr
                                key={row.symbol}
                                className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                            >
                                <td className="px-6 py-4 whitespace-nowrap font-bold text-slate-900 dark:text-white">
                                    #{row.universe_rank}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap font-bold text-green-600 dark:text-emerald-400">
                                    {row.symbol}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-slate-600 dark:text-slate-400">
                                    {row.sector}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-slate-900 dark:text-white">
                                    {row.forge_ultra_signal.toFixed(2)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <ScoreBadge score={row.momentum_z_score} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <ScoreBadge score={row.fundamental_z_score} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <ScoreBadge score={row.sentiment_z_score} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-slate-500 dark:text-slate-400">
                                    {(row.annualized_volatility * 100).toFixed(1)}%
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default RankingTable;
